/**
 * StatCard — стат-карточка верхних плашек разделов («Аналитика», «Проекты»):
 * заголовок + чип динамики, крупное значение, единица, строки-футеры.
 *
 * Значение «—» (или null) = данных с бэка нет: карточка показывает только
 * название и серую подпись «нет данных» — без чипа динамики, единицы и
 * футеров (замечание ревью от 14.08.2026).
 *
 * Низ карточки — три фиксированных строки (значение / единица / футер),
 * прижатых к низу через mt-auto: единица и футер резервируют высоту даже
 * пустыми, поэтому по всем карточкам ряда значения, единицы и футеры
 * лежат на одних линиях независимо от высоты заголовка и наличия чипа.
 * danger — красное значение (например, отток миграции).
 *
 * `icon` переключает карточку на раскладку макета «Строительства»: знак
 * показателя, значение и единица стоят одной строкой, отдельной строки
 * под единицу нет. Ряд из таких карточек выравнивается по низу за счёт
 * `mt-auto`, как и прежний.
 */
import { TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

export interface StatCardTrend {
  /** Текст чипа как в макете («+1.2%», «-1.2%», «+1.8»). */
  text?: string;
  /** Стрелка вниз (чип остаётся зелёным). */
  down?: boolean;
  /**
   * Негативная динамика — красный чип. Нужен там, где направление
   * приходит из данных, а не задано вёрсткой: ИФО ниже 100 % (US-17
   * FR-2.5). Без флага чип зелёный, как был.
   */
  danger?: boolean;
}

export interface StatCardFootnote {
  /** Приглушённая подпись перед значением («ИФО» на макете). */
  label?: string;
  text: string;
  /** Приглушённый хвост после значения («к янв.–июлю 2025»). */
  suffix?: string;
  /** Зелёная строка — положительная динамика («ИФО 100,7 %»). */
  success?: boolean;
  /** Жирная тёмная строка («1,82 млн. тенге»). */
  bold?: boolean;
  /** Красная строка («отток (к 05.05.2026)»). */
  danger?: boolean;
}

interface Props {
  title: string;
  /** Подсказка к заголовку: полное наименование показателя источника. */
  titleHint?: string;
  /**
   * Знак показателя слева от значения (макет «Строительства»). С иконкой
   * карточка переключается на раскладку макета: знак, значение и единица
   * стоят одной строкой. Без иконки — прежняя раскладка, где единица
   * занимает свою строку и выравнивает значения соседей по ряду.
   */
  icon?: LucideIcon;
  /** «—» или null → режим «нет данных» (единица/тренд/футеры скрыты). */
  value: string | null;
  unit?: string | null;
  /** Красное значение. */
  danger?: boolean;
  trend?: StatCardTrend | null;
  footnotes?: StatCardFootnote[];
  testId: string;
}

export const StatCard = ({
  title,
  titleHint,
  icon: Icon,
  value,
  unit,
  danger = false,
  trend,
  footnotes = [],
  testId,
}: Props) => {
  const { t } = useTranslation();
  const noData = value === null || value === '—';
  const TrendIcon = trend?.down ? TrendingDown : TrendingUp;

  if (noData) {
    return (
      <div
        data-testid={testId}
        className="flex h-full min-h-[10rem] flex-col rounded-2xl border border-surface-border bg-gradient-to-b from-surface to-surface-page p-5 shadow-card"
      >
        <h3
          // Длинные слова (казахские названия показателей) переносятся
          // внутри слова, а не выходят за край узкой плитки.
          className="min-w-0 break-words text-sm font-extrabold leading-snug"
          title={titleHint}
        >
          {title}
        </h3>
        <p
          className="mt-auto pb-5 text-sm lowercase text-text-muted"
          data-testid={`${testId}-nodata`}
        >
          {t('common.noData')}
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className="flex h-full min-h-[10rem] flex-col rounded-2xl border border-surface-border bg-gradient-to-b from-surface to-surface-page p-5 shadow-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3
          // Длинные слова (казахские названия показателей) переносятся
          // внутри слова, а не выходят за край узкой плитки.
          className="min-w-0 break-words text-sm font-extrabold leading-snug"
          title={titleHint}
        >
          {title}
        </h3>
        {trend && (
          <span
            data-testid={`${testId}-trend`}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-full border bg-surface px-3 py-1 text-sm font-bold',
              trend.danger
                ? 'border-status-danger/30 text-status-danger'
                : 'border-brand-100 text-brand-600',
            )}
          >
            <TrendIcon className="size-4" />
            {trend.text}
          </span>
        )}
      </div>

      {/* Значение — одна строка, прижатая к низу карточки; со знаком
          показателя единица встаёт рядом с числом (макет), без знака —
          на свою строку, чтобы «человек» у соседних карточек ряда лежал
          на одной линии. */}
      <p
        className={cn(
          'mt-auto pt-3',
          Icon === undefined
            ? 'whitespace-nowrap'
            : 'flex flex-wrap items-baseline gap-x-2',
        )}
      >
        {Icon !== undefined && (
          <Icon
            className="size-6 shrink-0 self-center text-brand-600"
            strokeWidth={1.8}
            aria-hidden="true"
          />
        )}
        <span
          data-testid={`${testId}-value`}
          className={cn(
            'text-3xl font-extrabold tabular-nums leading-tight',
            danger ? 'text-status-danger' : 'text-chart-indigo',
          )}
        >
          {value}
        </span>
        {Icon !== undefined && unit != null && (
          <span className="min-w-0 text-sm text-text-muted">{unit}</span>
        )}
      </p>

      {Icon === undefined && (
        <p className="mt-1 min-h-5 truncate text-sm text-text-muted">
          {unit ?? ' '}
        </p>
      )}

      {/* Блок футеров рендерится всегда (min-h на одну строку): без него
          значение карточки без футера опускается ниже соседних. */}
      <div className="mt-2 min-h-5 space-y-0.5">
        {footnotes.map((note) => (
          <p key={`${note.label ?? ''}|${note.text}`} className="text-[13px]">
            {note.label !== undefined && (
              <span className="text-text-muted">{note.label} </span>
            )}
            <span
              className={cn(
                note.danger
                  ? 'font-bold text-status-danger'
                  : note.success
                    ? 'font-bold text-brand-600'
                    : note.bold
                      ? 'font-bold text-text'
                      : 'text-text-muted',
              )}
            >
              {note.text}
            </span>
            {note.suffix !== undefined && (
              <span className="text-text-muted"> {note.suffix}</span>
            )}
          </p>
        ))}
      </div>
    </div>
  );
};
